import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import combineExtensionProviders from '@atlaskit/editor-common/extensions/combine-extension-providers';
import type { ExtensionProvider } from '@atlaskit/editor-common/extensions/extension-provider';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import CardClient from '@atlaskit/link-provider/client';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { RendererWithAnalytics as AkRenderer } from '@atlaskit/renderer/renderer';
import { ExtensionMode } from '@atlassian/company-hub-common';
import { HubRendererWrapperGated } from '@atlassian/company-hub-common/hub-renderer-wrapper';
import { getSectionExtensionContents } from '@atlassian/hub-providers/constants';
import { sectionExtensionProvider } from '@atlassian/section-extension';

import { useHubExtensionHandlers } from '../../controllers/use-hub-extension-handlers';
import type { HubRendererProps } from './types';

export const HubRenderer = (props: HubRendererProps): React.JSX.Element => {
	const {
		product,
		appearance,
		additionalExtensionHandlers = {},
		PropsProvider,
		contentId,
		dataProviders,
	} = props;
	const [sectionsProvider, setSectionsProvider] = useState<ExtensionProvider | null>(null);

	const intl = useIntl();

	const hubExtensionHandlers = useHubExtensionHandlers({
		contentId,
		PropsProvider,
	});

	const allExtensionHandlers = useMemo(() => {
		return { ...hubExtensionHandlers, ...additionalExtensionHandlers };
	}, [hubExtensionHandlers, additionalExtensionHandlers]);

	useEffect(() => {
		const loadSectionsProvider = async () => {
			const defaultExtensionProviderProps = {
				contentId,
				propsProvider: PropsProvider,
				mode: ExtensionMode.RENDERER,
				getSectionExtensionContents,
			};

			const defaultExtensionInstanceProps = {
				intl,
				contentId,
			};

			const sectionsProviderInstance = await sectionExtensionProvider(
				defaultExtensionProviderProps,
			);
			const sectionsProvider = await sectionsProviderInstance(defaultExtensionInstanceProps);
			setSectionsProvider(sectionsProvider);
		};

		loadSectionsProvider();
	}, [contentId, intl, PropsProvider]);

	const providerFactory = useMemo(() => {
		const factory = dataProviders || ProviderFactory.create({});
		if (sectionsProvider) {
			factory.setProvider(
				'extensionProvider',
				Promise.resolve(combineExtensionProviders([sectionsProvider])),
			);
		}
		return factory;
	}, [sectionsProvider, dataProviders]);

	const smartCardClient = useMemo(() => new CardClient(), []);
	// Use the same document layout as the Help Hub editor within the max-width canvas.
	const useDocumentColumn = product === 'JSM' && appearance === 'max';

	return (
		<HubRendererWrapperGated
			data-testid="hub-renderer"
			product={product as 'JSM' | 'CSM'}
			appearance={appearance}
		>
			<SmartCardProvider product={product} client={smartCardClient}>
				<AkRenderer
					{...props}
					{...(useDocumentColumn && {
						appearance: 'full-page' as const,
						allowRendererContainerStyles: true,
						UNSTABLE_allowTableResizing: true,
						UNSTABLE_allowTableAlignment: true,
					})}
					allowCustomPanels={true}
					media={{
						allowLinking: true,
					}}
					extensionHandlers={allExtensionHandlers}
					// dataProviders supplies the extension provider factory which enables section extensions to be rendered within the hub
					dataProviders={providerFactory}
					adfStage="stage0"
				/>
			</SmartCardProvider>
		</HubRendererWrapperGated>
	);
};

export default HubRenderer;
